import { useContext, useState } from "react";
import { Chip, IconButton, TextField } from "@mui/material";
import { Box } from "@mui/system";
import MultipleSelect from "../MultipleSelect";
import SelectMenu from "../SelectMenu";
import TableFilterAcionts from "../TableFilterAcionts";
import CancelIcon from "@mui/icons-material/Cancel";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { AppContext } from "../../scripts/context/App";
import { filterSchema } from "./schema";

interface IFilter {
  status: boolean;
  data: {
    permissions: string;
    full_name: string;
    email: string;
    roles: string[];
    status: string;
  };
}

interface IProps {
  filter: IFilter;
  setFilter: (filter: IFilter) => void;
  handleSearch: (data?: IFilter) => void;
}

const initFilterData: IFilter = {
  status: true,
  data: {
    permissions: "",
    full_name: "",
    email: "",
    roles: [],
    status: "",
  },
};

const ERROR_INPUTS = {
  full_name: false,
  email: false,
};

export default function FilterForm({
  filter,
  setFilter,
  handleSearch,
}: IProps) {
  const { roles_permissions } = useContext(AppContext);
  const [errors, setErrors] = useState(ERROR_INPUTS);

  const handleChanges = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({
      ...filter,
      data: {
        ...filter.data,
        [event.target.name]: event.target.value,
      },
    });
  };
  const checkValidation = () => {
    setErrors(ERROR_INPUTS);

    filterSchema
      .validate(filter.data, { abortEarly: false })
      .then(() => {
        handleSearch();
      })
      .catch((error) => {
        setErrors(
          error.inner.reduce((acc: any, err: any) => {
            acc[err.path] = err.message;
            return acc;
          }, {})
        );
      });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
        <TextField
          label="Full Name"
          variant="standard"
          value={filter.data.full_name}
          name="full_name"
          onChange={handleChanges}
          sx={{ flex: "1 1 40%" }}
          error={Boolean(errors.full_name)} helperText={errors.full_name}
        />
        <TextField
          label="Email"
          variant="standard"
          value={filter.data.email}
          name="email"
          onChange={handleChanges}
          sx={{ flex: "1 1 40%" }}
          inputProps={{ type: "email" }}
          error={Boolean(errors.email)} helperText={errors.email}
        />
        <SelectMenu
          label="Custom Permissions"
          handleChange={({ target }) => {
            setFilter({
              ...filter,
              data: {
                ...filter.data,
                permissions: target.value,
              },
            });
          }}
          options={[
            { value: "", label: "Custom Permissions" },
            { value: "withPermission", label: "Has" },
            { value: "withoutPermission", label: "Has not" },
          ]}
          value={filter.data.permissions}
          sx={{ flex: "1 1 40%" }}
        />

        <SelectMenu
          label="Status"
          handleChange={({ target }) => {
            setFilter({
              ...filter,
              data: {
                ...filter.data,
                status: target.value,
              },
            });
          }}
          options={[
            { value: "", label: "Status" },
            { value: "Active", label: "Active" },
            { value: "Inactive", label: "InActive" },
          ]}
          value={filter.data.status}
          sx={{ flex: "1 1 40%" }}
        />
        <MultipleSelect
          label="Roles"
          handleChange={({ target }: any) => {
            const newRoles =
              typeof target.value === "string"
                ? target.value.split(",")
                : target.value;

            setFilter({
              ...filter,
              data: {
                ...filter.data,
                roles: newRoles,
              },
            });
          }}
          value={filter.data.roles}
          options={roles_permissions
            .filter((role: any) => role.status === "Active")
            .map((role: any) => ({
              value: role.name,
              label: role.name,
            }))}
          renderValue={(selected) => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    variant="outlined"
                    key={value}
                    label={value}
                    onDelete={() => {
                      const newRoles = filter.data.roles.filter(
                        (role) => role !== value
                      );
                      setFilter({
                        ...filter,
                        data: {
                          ...filter.data,
                          roles: newRoles,
                        },
                      });
                    }}
                    clickable
                    deleteIcon={
                      <CancelIcon
                        onMouseDown={(event: any) => event.stopPropagation()}
                      />
                    }
                  />
                ))}
              </Box>
              {selected.length > 0 && (
                <IconButton
                  onMouseDown={(event: any) => event.stopPropagation()}
                  onClick={() => {
                    setFilter({
                      ...filter,
                      data: {
                        ...filter.data,
                        roles: [],
                      },
                    });
                  }}
                >
                  <HighlightOffIcon color="error" />
                </IconButton>
              )}
            </Box>
          )}
          sx={{ flex: "1 1 40%" }}
        />
      </Box>
      <TableFilterAcionts
        disableCancel={false}
        handleClearSearch={() => {
          setFilter(initFilterData);

          handleSearch(initFilterData);
        }}
        handleSearch={checkValidation}
      />
    </Box>
  );
}
